/**
 * @fileoverview Collapsible RAG results component for query messages
 * 
 * @purpose Displays RAG search results in a collapsible section within query messages
 * 
 * @usage Used by AgentMessage component to show RAG results
 * 
 * @ownership KorvoChat component infrastructure
 * 
 * @restrictions 
 * - Only used for query messages, not helper messages
 */

import React from 'react';

const ChevronDownIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const ChevronUpIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
  </svg>
);

export type RagResultsCollapsibleProps = {
  ragResults: Array<{ content: string; title?: string; similarity?: number; sourceId?: string | number }>;
  messageId: string;
}

export function RagResultsCollapsible({ ragResults, messageId }: RagResultsCollapsibleProps) {
  const [isExpanded, setIsExpanded] = React.useState(false);

  return (
    <div className="mt-2 border-t border-gray-300 pt-2">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-800 w-full text-left"
        aria-expanded={isExpanded}
        aria-controls={`rag-content-${messageId}`}
      >
        {isExpanded ? <ChevronUpIcon className="w-3 h-3" /> : <ChevronDownIcon className="w-3 h-3" />}
        <span className="font-medium">RAG Results ({ragResults.length})</span>
      </button>
      {isExpanded && (
        <div id={`rag-content-${messageId}`} className="mt-2 space-y-2 max-h-96 overflow-y-auto">
          {ragResults.map((result, index) => (
            <div key={index} className="px-2 py-1.5 bg-gray-50 border border-gray-200 rounded text-xs">
              <div className="flex items-start justify-between mb-1">
                <span className="font-medium text-gray-700">{result.title || `Result ${index + 1}`}</span>
                {typeof result.similarity === 'number' && (
                  <span className="text-gray-500 ml-2">{(result.similarity * 100).toFixed(1)}%</span>
                )}
              </div>
              <div className="text-gray-600 whitespace-pre-wrap text-xs leading-relaxed">{result.content}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

